import type {
  TableCellData,
  TableCellProperties,
  TableData,
  TableMergedArea,
  TableSemanticHints,
  TableStyleSettingsData,
  TableStyleSnapshot,
} from "../types";
import {
  extractBorderWeight,
  extractExplicitBorderColor,
  normalizeExtractedColorToken,
} from "./factsetExtractorFormatting";

function readTextFrameText(textFrame: unknown): string {
  if (!textFrame || typeof textFrame !== "object") return "";
  const frame = textFrame as { text?: unknown; paragraphs?: unknown };
  if (typeof frame.text === "string") return frame.text;
  if (!Array.isArray(frame.paragraphs)) return "";
  const paragraphTexts: string[] = [];
  for (const paragraph of frame.paragraphs) {
    if (!paragraph || typeof paragraph !== "object") {
      paragraphTexts.push("");
      continue;
    }
    const para = paragraph as { text?: unknown; runs?: unknown };
    if (typeof para.text === "string") {
      paragraphTexts.push(para.text);
      continue;
    }
    if (!Array.isArray(para.runs)) {
      paragraphTexts.push("");
      continue;
    }
    const runTexts = para.runs.map((run) => {
      if (!run || typeof run !== "object") return "";
      const runText = (run as { text?: unknown }).text;
      return typeof runText === "string" ? runText : "";
    });
    paragraphTexts.push(runTexts.join(""));
  }
  return paragraphTexts.join("\n");
}

function toTableCellText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (!value || typeof value !== "object") return "";

  const obj = value as Record<string, unknown>;

  // Flat-cell shorthand (used by legacy/test payloads and host responses that
  // pre-flatten the cell to a single text property).
  const flat = obj.text ?? obj.value ?? obj.displayText ?? obj.formattedValue ?? obj.cellText;
  if (typeof flat === "string") return flat;
  if (typeof flat === "number" || typeof flat === "boolean") return String(flat);

  // f2 RunOfficeExtractor TableCell shape:
  //   TableCell { shape?: Shape; borders?: ... }
  //   Shape.textFrame.paragraphs[*].runs[*].text
  // (see f2_docs/powerpoint/office-extractor/schema/TableCell.md and Shape.md).
  // Without this branch the parser cannot read any cell text from the host's
  // default extractor response and tables render as a grid of empty strings
  // in the slide context.
  const shape = obj.shape as { textFrame?: unknown } | undefined;
  const textFrame = shape?.textFrame ?? (obj as { textFrame?: unknown }).textFrame;
  return readTextFrameText(textFrame);
}

function toTableRow(row: unknown): string[] {
  if (Array.isArray(row)) return row.map(toTableCellText);
  if (row && typeof row === "object") {
    const obj = row as Record<string, unknown>;
    if (Array.isArray(obj.cells)) return obj.cells.map(toTableCellText);
    if (Array.isArray(obj.values)) return obj.values.map(toTableCellText);
  }
  const text = toTableCellText(row);
  return text.length > 0 ? [text] : [];
}

export function buildTableTextUpdatesFromRows(
  rows: string[][]
): NonNullable<TableData["textUpdates"]> {
  const updates: NonNullable<TableData["textUpdates"]> = [];
  for (let row = 0; row < rows.length; row += 1) {
    const rowValues = rows[row] || [];
    for (let col = 0; col < rowValues.length; col += 1) {
      updates.push({ row, col, text: rowValues[col] ?? "" });
    }
  }
  return updates;
}

export function buildEmptyTableShellRows(rows: string[][]): string[][] {
  return rows.map((row) => row.map(() => ""));
}

export function mergeTableTextUpdates(
  base: NonNullable<TableData["textUpdates"]>,
  overrides?: NonNullable<TableData["textUpdates"]>
): NonNullable<TableData["textUpdates"]> {
  const merged = new Map<string, NonNullable<TableData["textUpdates"]>[number]>();
  for (const update of base || []) {
    merged.set(`${update.row}:${update.col}`, update);
  }
  for (const update of overrides || []) {
    merged.set(`${update.row}:${update.col}`, update);
  }
  return Array.from(merged.values()).sort((left, right) =>
    left.row === right.row ? left.col - right.col : left.row - right.row
  );
}

function readFiniteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function readNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function cloneJsonValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function readUnitInterval(value: unknown): number | undefined {
  const numeric = readFiniteNumber(value);
  return numeric !== undefined && numeric >= 0 && numeric <= 1 ? numeric : undefined;
}

function readPositiveNumber(value: unknown): number | undefined {
  const numeric = readFiniteNumber(value);
  return numeric !== undefined && numeric > 0 ? numeric : undefined;
}

function normalizeHorizontalAlignment(
  value: unknown
): TableCellProperties["horizontalAlignment"] | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (normalized.includes("distributed")) return "distributed";
  if (normalized.includes("justify")) return "justify";
  if (normalized.includes("center") || normalized.includes("centre")) return "center";
  if (normalized.includes("right")) return "right";
  if (normalized.includes("left")) return "left";
  return undefined;
}

function normalizeVerticalAlignment(
  value: unknown
): TableCellProperties["verticalAlignment"] | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (normalized.includes("middle") || normalized.includes("center")) return "middle";
  if (normalized.includes("bottom")) return "bottom";
  if (normalized.includes("top")) return "top";
  return undefined;
}

function extractCellFill(shape: Record<string, unknown>): TableCellProperties["fill"] | undefined {
  const fill = asRecord(shape.fill ?? shape.fillFormat);
  if (!fill) return undefined;

  const type = typeof fill.type === "string" ? fill.type.trim().toLowerCase() : "";
  const isNoFill = fill.visible === false || type === "nofill" || type === "none";
  const color = isNoFill ? undefined : normalizeExtractedColorToken(fill);
  const transparency = isNoFill ? 1 : readUnitInterval(fill.transparency);
  if (color === undefined && transparency === undefined) return undefined;

  return {
    ...(color !== undefined ? { color } : {}),
    ...(transparency !== undefined ? { transparency } : {}),
  };
}

function extractFirstRunFont(shape: Record<string, unknown>): Record<string, unknown> | undefined {
  const textFrame = asRecord(shape.textFrame);
  const paragraphs = Array.isArray(textFrame?.paragraphs)
    ? textFrame.paragraphs
    : Array.isArray(shape.paragraphs)
      ? shape.paragraphs
      : [];
  for (const paragraphValue of paragraphs) {
    const paragraph = asRecord(paragraphValue);
    if (!Array.isArray(paragraph?.runs)) continue;
    for (const runValue of paragraph.runs) {
      const run = asRecord(runValue);
      const runFormat = asRecord(run?.runFormat);
      const font = asRecord(runFormat?.font ?? run?.font);
      if (font) return font;
    }
  }
  return asRecord(shape.font);
}

function extractSupportedFontProperties(
  font: Record<string, unknown> | undefined,
  fallback?: Record<string, unknown>
): TableCellProperties["font"] | undefined {
  const color = font
    ? normalizeExtractedColorToken(font.fill ?? font.color ?? font)
    : normalizeExtractedColorToken(fallback?.textColor ?? fallback?.fontColor);
  const name = readNonEmptyString(
    font?.name ?? font?.family ?? fallback?.fontFamily ?? fallback?.fontName
  );
  const size = readPositiveNumber(font?.size ?? fallback?.fontSize);
  const bold = readBoolean(font?.bold ?? fallback?.bold);
  const italic = readBoolean(font?.italic ?? fallback?.italic);
  const allCaps = readBoolean(font?.allCaps ?? fallback?.allCaps);
  if (
    color === undefined &&
    name === undefined &&
    size === undefined &&
    bold === undefined &&
    italic === undefined &&
    allCaps === undefined
  ) {
    return undefined;
  }

  return {
    ...(color !== undefined ? { color } : {}),
    ...(name !== undefined ? { name } : {}),
    ...(size !== undefined ? { size } : {}),
    ...(bold !== undefined ? { bold } : {}),
    ...(italic !== undefined ? { italic } : {}),
    ...(allCaps !== undefined ? { allCaps } : {}),
  };
}

function extractCellFont(shape: Record<string, unknown>): TableCellProperties["font"] | undefined {
  return extractSupportedFontProperties(extractFirstRunFont(shape), shape);
}

function extractCellTextRuns(
  shape: Record<string, unknown>
): TableCellProperties["textRuns"] | undefined {
  const textFrame = asRecord(shape.textFrame);
  const paragraphs = Array.isArray(textFrame?.paragraphs)
    ? textFrame.paragraphs
    : Array.isArray(shape.paragraphs)
      ? shape.paragraphs
      : [];
  const textRuns: NonNullable<TableCellProperties["textRuns"]> = [];
  let hasFormattedRun = false;

  for (const paragraphValue of paragraphs) {
    const paragraph = asRecord(paragraphValue);
    if (!Array.isArray(paragraph?.runs)) continue;
    for (const runValue of paragraph.runs) {
      const run = asRecord(runValue);
      if (!run || typeof run.text !== "string") continue;
      const runFormat = asRecord(run.runFormat);
      const font = extractSupportedFontProperties(asRecord(runFormat?.font ?? run.font));
      hasFormattedRun = hasFormattedRun || Boolean(font);
      textRuns.push({
        text: run.text,
        ...(font ? { font } : {}),
      });
    }
  }

  return textRuns.length > 1 && hasFormattedRun ? textRuns : undefined;
}

function findCaseInsensitiveProperty(
  record: Record<string, unknown> | undefined,
  key: string
): unknown {
  if (!record) return undefined;
  const match = Object.keys(record).find((candidate) => candidate.toLowerCase() === key);
  return match ? record[match] : undefined;
}

function extractCellBorder(
  value: unknown
): NonNullable<TableCellProperties["borders"]>[keyof NonNullable<
  TableCellProperties["borders"]
>] {
  const border = asRecord(value);
  if (!border) return undefined;

  const hidden = border.visible === false;
  const visible = readBoolean(border.visible);
  const transparency = readUnitInterval(border.transparency);
  const color = hidden
    ? undefined
    : normalizeExtractedColorToken(extractExplicitBorderColor(border));
  const extractedWeight = extractBorderWeight(border);
  const weight = hidden ? 0 : extractedWeight;
  const effectiveVisible =
    hidden ||
    (transparency !== undefined && transparency >= 1) ||
    (extractedWeight !== undefined && extractedWeight <= 0)
      ? false
      : visible === true
        ? true
        : undefined;
  if (
    color === undefined &&
    weight === undefined &&
    transparency === undefined &&
    visible === undefined &&
    effectiveVisible === undefined
  ) {
    return undefined;
  }

  return {
    ...(color !== undefined ? { color } : {}),
    ...(weight !== undefined ? { weight } : {}),
    ...(transparency !== undefined ? { transparency } : {}),
    ...(visible !== undefined ? { visible } : {}),
    ...(effectiveVisible !== undefined ? { effectiveVisible } : {}),
  };
}

function extractCellBorders(
  cell: Record<string, unknown>,
  shape: Record<string, unknown>
): TableCellProperties["borders"] | undefined {
  const source = asRecord(cell.borders ?? shape.borders);
  if (!source) return undefined;

  const borders: NonNullable<TableCellProperties["borders"]> = {};
  for (const side of ["top", "bottom", "left", "right"] as const) {
    const normalized = extractCellBorder(findCaseInsensitiveProperty(source, side));
    if (normalized) borders[side] = normalized;
  }
  return Object.keys(borders).length > 0 ? borders : undefined;
}

function extractCellTextFrameFormat(
  shape: Record<string, unknown>
): Record<string, unknown> | undefined {
  const textFrame = asRecord(shape.textFrame);
  return asRecord(textFrame?.textFrameFormat ?? shape.textFrameFormat);
}

function extractFirstParagraphFormat(
  shape: Record<string, unknown>
): Record<string, unknown> | undefined {
  const textFrame = asRecord(shape.textFrame);
  const paragraphs = Array.isArray(textFrame?.paragraphs)
    ? textFrame.paragraphs
    : Array.isArray(shape.paragraphs)
      ? shape.paragraphs
      : [];
  return asRecord(asRecord(paragraphs[0])?.paragraphFormat);
}

function extractCellMargins(
  textFrameFormat: Record<string, unknown> | undefined
): TableCellProperties["margins"] | undefined {
  if (!textFrameFormat) return undefined;
  const margins: NonNullable<TableCellProperties["margins"]> = {};
  for (const side of ["left", "right", "top", "bottom"] as const) {
    const value = readFiniteNumber(
      textFrameFormat[`margin${side[0].toUpperCase()}${side.slice(1)}`] ??
        textFrameFormat[side]
    );
    if (value !== undefined) margins[side] = value;
  }
  return Object.keys(margins).length > 0 ? margins : undefined;
}

function extractCanonicalCellProperties(value: unknown): TableCellProperties {
  const cell = asRecord(value);
  if (!cell) return {};
  const shape = asRecord(cell.shape) ?? cell;
  const paragraphFormat = extractFirstParagraphFormat(shape);
  const textFrameFormat = extractCellTextFrameFormat(shape);

  const properties: TableCellProperties = {};
  const fill = extractCellFill(shape);
  const font = extractCellFont(shape);
  const textRuns = extractCellTextRuns(shape);
  const borders = extractCellBorders(cell, shape);
  const horizontalAlignment = normalizeHorizontalAlignment(
    paragraphFormat?.alignment ?? shape.horizontalAlignment
  );
  const verticalAlignment = normalizeVerticalAlignment(
    textFrameFormat?.verticalAnchor ?? shape.verticalAlignment
  );
  const indentLevel = readFiniteNumber(paragraphFormat?.indentLevel ?? shape.indentLevel);
  const margins = extractCellMargins(textFrameFormat);

  if (fill) properties.fill = fill;
  if (font) properties.font = font;
  if (textRuns) properties.textRuns = textRuns;
  if (borders) properties.borders = borders;
  if (horizontalAlignment) properties.horizontalAlignment = horizontalAlignment;
  if (verticalAlignment) properties.verticalAlignment = verticalAlignment;
  if (indentLevel !== undefined) properties.indentLevel = indentLevel;
  if (margins) properties.margins = margins;
  return properties;
}

function extractRawCellGrid(
  rawRows: unknown[],
  rowCount: number,
  colCount: number
): unknown[][] {
  const grid: unknown[][] = [];
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const rawRow = rawRows[rowIndex];
    const rowRecord = asRecord(rawRow);
    const values = Array.isArray(rawRow)
      ? rawRow
      : Array.isArray(rowRecord?.cells)
        ? rowRecord.cells
        : Array.isArray(rowRecord?.values)
          ? rowRecord.values
          : rawRow === undefined
            ? []
            : [rawRow];
    grid.push(Array.from({ length: colCount }, (_, colIndex) => values[colIndex]));
  }
  return grid;
}

function deepEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function intersectKnownValues(values: unknown[]): unknown {
  if (values.length === 0 || values.some((value) => value === undefined)) return undefined;
  const records = values.map(asRecord);
  if (records.every((record): record is Record<string, unknown> => Boolean(record))) {
    const first = records[0];
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(first)) {
      if (!records.every((record) => hasOwn(record, key))) continue;
      const intersection = intersectKnownValues(records.map((record) => record[key]));
      if (intersection !== undefined) result[key] = intersection;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }
  return values.every((value) => deepEqual(value, values[0]))
    ? cloneJsonValue(values[0])
    : undefined;
}

function subtractCommonValues(
  value: Record<string, unknown>,
  common: Record<string, unknown>
): Record<string, unknown> {
  const delta: Record<string, unknown> = {};
  for (const [key, candidate] of Object.entries(value)) {
    if (!hasOwn(common, key)) {
      delta[key] = cloneJsonValue(candidate);
      continue;
    }
    const candidateRecord = asRecord(candidate);
    const commonRecord = asRecord(common[key]);
    if (candidateRecord && commonRecord) {
      const nested = subtractCommonValues(candidateRecord, commonRecord);
      if (Object.keys(nested).length > 0) delta[key] = nested;
    } else if (!deepEqual(candidate, common[key])) {
      delta[key] = cloneJsonValue(candidate);
    }
  }
  return delta;
}

function extractResolvedCellStyleSnapshot(
  rawRows: unknown[],
  rowCount: number,
  colCount: number
): Pick<
  TableStyleSnapshot,
  "uniformCellProperties" | "cellOverrides" | "cellOverrideCount"
> | null {
  const canonicalCells = extractRawCellGrid(rawRows, rowCount, colCount).map((row) =>
    row.map(extractCanonicalCellProperties)
  );
  const flattened = canonicalCells.flat();
  if (!flattened.some((cell) => Object.keys(cell).length > 0)) return null;

  const uniform =
    (intersectKnownValues(flattened) as TableCellProperties | undefined) ?? {};
  const uniformRecord = uniform as Record<string, unknown>;
  const cellOverrides: NonNullable<TableStyleSnapshot["cellOverrides"]> = [];
  for (let row = 0; row < canonicalCells.length; row += 1) {
    for (let col = 0; col < canonicalCells[row].length; col += 1) {
      const delta = subtractCommonValues(
        canonicalCells[row][col] as Record<string, unknown>,
        uniformRecord
      ) as TableCellProperties;
      if (Object.keys(delta).length > 0) {
        cellOverrides.push({ row, col, set: delta });
      }
    }
  }

  return {
    ...(Object.keys(uniform).length > 0 ? { uniformCellProperties: uniform } : {}),
    ...(cellOverrides.length > 0 ? { cellOverrides } : {}),
    cellOverrideCount: cellOverrides.length,
  };
}

/**
 * Pull column widths from either:
 *   - a pre-Finster-shape `columnWidths: number[]` (internal callsites), or
 *   - a wire-shape `columns: TableColumn[]` (f2 RunOfficeExtractor), where
 *     `TableColumn { width?: number }` per f2_docs/powerpoint/office-extractor/schema/TableColumn.md.
 *
 * Returns undefined when no width information is available so we don't emit
 * an array full of undefineds into the slide context.
 */
function extractColumnWidths(obj: Record<string, unknown>, colCount: number): number[] | undefined {
  if (Array.isArray(obj.columnWidths)) {
    const widths = obj.columnWidths.map(readFiniteNumber);
    if (widths.some((w) => typeof w === "number")) {
      return widths.map((w) => (typeof w === "number" ? w : 0)).slice(0, colCount);
    }
  }
  if (Array.isArray(obj.columns)) {
    const widths = obj.columns.map((col) => {
      if (col && typeof col === "object") {
        return readFiniteNumber((col as { width?: unknown }).width);
      }
      return undefined;
    });
    if (widths.some((w) => typeof w === "number")) {
      return widths.map((w) => (typeof w === "number" ? w : 0)).slice(0, colCount);
    }
  }
  return undefined;
}

/**
 * Pull row heights from either:
 *   - a pre-Finster-shape `rowHeights: number[]`, or
 *   - a wire-shape array of `TableRow { height?: number }` objects (per
 *     f2_docs/powerpoint/office-extractor/schema/TableRow.md).
 *
 * `rawRows` is the original `obj.rows` before flattening, so we can still
 * read `.height` on each TableRow even after the per-cell text is mapped out.
 */
function extractRowHeights(
  obj: Record<string, unknown>,
  rawRows: unknown[],
  rowCount: number
): number[] | undefined {
  if (Array.isArray(obj.rowHeights)) {
    const heights = obj.rowHeights.map(readFiniteNumber);
    if (heights.some((h) => typeof h === "number")) {
      return heights.map((h) => (typeof h === "number" ? h : 0)).slice(0, rowCount);
    }
  }
  const heights = rawRows.map((row) => {
    if (row && typeof row === "object") {
      return readFiniteNumber((row as { height?: unknown }).height);
    }
    return undefined;
  });
  if (heights.some((h) => typeof h === "number")) {
    return heights.map((h) => (typeof h === "number" ? h : 0)).slice(0, rowCount);
  }
  return undefined;
}

/**
 * Pull merged-cell anchors. The f2 RunOfficeExtractor schema does not expose
 * merge information on `TableCell` (as of 2026-05-18); we honor input that
 * already carries `mergedAreas` in the Finster shape for internal callsites
 * and forward-compat. If the host ever adds merge info to its schema, the
 * extraction can be added here.
 */
function extractMergedAreas(obj: Record<string, unknown>): TableMergedArea[] | undefined {
  if (!Array.isArray(obj.mergedAreas)) return undefined;
  const areas: TableMergedArea[] = [];
  for (const candidate of obj.mergedAreas) {
    if (!candidate || typeof candidate !== "object") continue;
    const m = candidate as Record<string, unknown>;
    const rowIndex = readFiniteNumber(m.rowIndex);
    const columnIndex = readFiniteNumber(m.columnIndex);
    const rowCount = readFiniteNumber(m.rowCount);
    const columnCount = readFiniteNumber(m.columnCount);
    if (
      typeof rowIndex === "number" &&
      typeof columnIndex === "number" &&
      typeof rowCount === "number" &&
      typeof columnCount === "number"
    ) {
      areas.push({ rowIndex, columnIndex, rowCount, columnCount });
    }
  }
  return areas.length > 0 ? areas : undefined;
}

/**
 * Translate the wire-level table-style flags into the Finster `TableStyleSnapshot`
 * shape (which mirrors `TableStyleSettingsData` from the write-side contract).
 * Pulls:
 *   - `style.name` (or `.id`) → tableStyle name token
 *   - `horizBanding` / `vertBanding` → banding toggles
 *   - `firstRow` / `firstCol` / `lastRow` / `lastCol` → highlight toggles
 *
 * Also honors pre-Finster-shape input where the caller has already supplied
 * `styleSnapshot` directly.
 */
function extractStyleSnapshot(obj: Record<string, unknown>): TableStyleSnapshot | undefined {
  if (obj.styleSnapshot && typeof obj.styleSnapshot === "object") {
    return obj.styleSnapshot as TableStyleSnapshot;
  }

  const styleSettings: TableStyleSettingsData = {};
  const styleObj = obj.style && typeof obj.style === "object" ? (obj.style as Record<string, unknown>) : undefined;
  const tableStyleName = readNonEmptyString(styleObj?.name) ?? readNonEmptyString(styleObj?.id);
  if (tableStyleName) styleSettings.style = tableStyleName;

  const areRowsBanded = readBoolean(obj.horizBanding);
  if (areRowsBanded !== undefined) styleSettings.areRowsBanded = areRowsBanded;
  const areColumnsBanded = readBoolean(obj.vertBanding);
  if (areColumnsBanded !== undefined) styleSettings.areColumnsBanded = areColumnsBanded;
  const isFirstRow = readBoolean(obj.firstRow);
  if (isFirstRow !== undefined) styleSettings.isFirstRowHighlighted = isFirstRow;
  const isFirstCol = readBoolean(obj.firstCol);
  if (isFirstCol !== undefined) styleSettings.isFirstColumnHighlighted = isFirstCol;
  const isLastRow = readBoolean(obj.lastRow);
  if (isLastRow !== undefined) styleSettings.isLastRowHighlighted = isLastRow;
  const isLastCol = readBoolean(obj.lastCol);
  if (isLastCol !== undefined) styleSettings.isLastColumnHighlighted = isLastCol;

  const hasAnyStyleSettings = Object.keys(styleSettings).length > 0;
  if (!tableStyleName && !hasAnyStyleSettings) return undefined;

  const snapshot: TableStyleSnapshot = {};
  if (tableStyleName) snapshot.tableStyle = tableStyleName;
  if (hasAnyStyleSettings) snapshot.styleSettings = styleSettings;
  return snapshot;
}

/**
 * Derive semantic hints from the wire-level flags. Right now the only
 * inferable hint is `headerRowIndices: [0]` when `firstRow: true`, which
 * matches PowerPoint's "first row is a header" style flag. `numericColumnIndices`
 * is intentionally left to a separate cell-text-scanning pass.
 *
 * Honors pre-Finster-shape input that has supplied `semanticHints` directly.
 */
function isNumericLikeCellValue(value: string): boolean {
  const cleaned = value.replace(/[$,%\s,()]/g, "").replace(/−/g, "-");
  return /^[-+]?(?:\d+\.?\d*|\.\d+)(?:[KMBT])?$/i.test(cleaned);
}

function extractSemanticHints(
  obj: Record<string, unknown>,
  rows: string[][]
): TableSemanticHints | undefined {
  if (obj.semanticHints && typeof obj.semanticHints === "object") {
    return obj.semanticHints as TableSemanticHints;
  }

  const headerRowIndices = obj.firstRow === true ? [0] : [];
  const startDataRow = headerRowIndices.length > 0 ? 1 : 0;
  const numericColumnIndices: number[] = [];
  const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  for (let col = 0; col < colCount; col += 1) {
    let numericCount = 0;
    let nonEmptyCount = 0;
    for (let row = startDataRow; row < rows.length; row += 1) {
      const value = rows[row]?.[col] ?? "";
      if (!value.trim()) continue;
      nonEmptyCount += 1;
      if (isNumericLikeCellValue(value)) numericCount += 1;
    }
    if (nonEmptyCount > 0 && numericCount / nonEmptyCount >= 0.6) {
      numericColumnIndices.push(col);
    }
  }

  if (headerRowIndices.length === 0 && numericColumnIndices.length === 0) return undefined;
  return {
    ...(headerRowIndices.length > 0 ? { headerRowIndices } : {}),
    ...(numericColumnIndices.length > 0 ? { numericColumnIndices } : {}),
  };
}

export function normalizeTableCellData(tableData: unknown): TableCellData | null {
  if (!tableData || typeof tableData !== "object") return null;
  const obj = tableData as Record<string, unknown>;
  if (!Array.isArray(obj.rows)) return null;

  const rawRows = obj.rows;
  const rows = rawRows.map(toTableRow);
  const inferredColCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const colCount =
    typeof obj.colCount === "number" && obj.colCount >= 0 ? obj.colCount : inferredColCount;
  const normalizedRows = rows.map((row) => {
    if (row.length >= colCount) return row.slice(0, colCount);
    const padded = [...row];
    while (padded.length < colCount) padded.push("");
    return padded;
  });
  const declaredRowCount =
    typeof obj.rowCount === "number" && obj.rowCount >= 0 ? obj.rowCount : normalizedRows.length;
  const finalRows = [...normalizedRows];
  if (finalRows.length > declaredRowCount) {
    finalRows.length = declaredRowCount;
  } else {
    while (finalRows.length < declaredRowCount) {
      finalRows.push(Array(colCount).fill(""));
    }
  }
  const rowCount = finalRows.length;

  // Metadata extraction. Each helper translates the wire-level shape from
  // f2_docs (Table / TableRow / TableColumn / TableStyle) into the Finster
  // TableCellData shape, AND honors pre-Finster-shape input where the caller
  // already supplied the field directly. Anything missing/empty is omitted
  // rather than emitted as undefined to keep the slide context tight.
  const result: TableCellData = { rowCount, colCount, rows: finalRows };
  const columnWidths = extractColumnWidths(obj, colCount);
  if (columnWidths) result.columnWidths = columnWidths;
  const rowHeights = extractRowHeights(obj, rawRows, rowCount);
  if (rowHeights) result.rowHeights = rowHeights;
  const mergedAreas = extractMergedAreas(obj);
  if (mergedAreas) result.mergedAreas = mergedAreas;
  const styleSnapshot = extractStyleSnapshot(obj);
  const resolvedCellStyles = extractResolvedCellStyleSnapshot(rawRows, rowCount, colCount);
  if (styleSnapshot || resolvedCellStyles) {
    result.styleSnapshot = {
      ...(styleSnapshot ?? {}),
      ...(resolvedCellStyles ?? {}),
    };
  }
  const semanticHints = extractSemanticHints(obj, finalRows);
  if (semanticHints) result.semanticHints = semanticHints;
  return result;
}

